import { CobrancaContaDetailsItem } from "core/features/cobranca/typings";
import {
  CobrancaContaItemComposicao,
  CobrancaDetailsExportPdfExcelDataType,
} from "features/cobranca/typings";
import { defaultContentMapperXlsx } from "infra/packages/json-as-xlsx/index";
import xlsx, { IColumn, IContent, IJsonSheet, ISettings } from "json-as-xlsx";
import moment from "moment";
import { sanitizeSheetName } from "infra/helpers/sanitizeSheetName";

export function exportExcelRelatorioCobrancas({
  data,
}: {
  data: CobrancaDetailsExportPdfExcelDataType;
}) {
  const valores = data?.contas.reduce(
    (acc, next) => {
      return {
        original: (acc.original += next.valor),
        total: (acc.total += next.total),
      };
    },
    { original: 0, total: 0 }
  );

  const settings: ISettings = {
    fileName: `Cobranças - Unidade ${data?.numero}`,
    extraLength: 3,
    writeOptions: {},
  };

  const columnsResumo = getColumnsResumo();

  const columnsTotais = getColumnsTotais();

  const columnsConta = getColumnsConta();

  const sheets: IJsonSheet[] = [];

  sheets.push({
    sheet: `Resumo`,
    columns: columnsResumo,
    content: [
      {
        unidade: data.nomeUnidade || "",
        valorOriginal: valores.original,
        total: valores.total,
        dataCalculo: data.dataCalculo || "",
      },
    ],
  });

  sheets.push({
    sheet: `Totais`,
    columns: columnsTotais,
    content: data.contas.map(defaultContentMapperXlsx),
  });

  data.contas.forEach((item, index) => {
    sheets.push({
      sheet: item.descricao
        ? sanitizeSheetName(`[${index + 1}] ${item.descricao} `.slice(0, 31))
        : `Conta Sem Descrição ${index + 1}`,
      columns: columnsConta,
      content: item.composicoes.map(defaultContentMapperXlsx),
    });
  });

  xlsx(sheets, settings);
}

function getColumnsConta() {
  const columns: IColumn[] = [];

  columns.push({
    label: "Nome",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaItemComposicao).nome,
  });

  columns.push({
    label: "Original",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaItemComposicao).valor,
  });

  columns.push({
    label: "Multa",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaItemComposicao).multa,
  });

  columns.push({
    label: "Juros",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaItemComposicao).juros,
  });

  columns.push({
    label: "Tx. Cobrança",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaItemComposicao).valorTaxaCobranca,
  });

  columns.push({
    label: "Correção Monetária",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaItemComposicao).atualizacaoMonetaria,
  });

  // columns.push({
  //   label: "Honorários",
  //   format: "#,###0.00",
  //   value: (row: IContent) =>
  //   (row as unknown as CobrancaContaItemComposicao).honorarios
  // });

  columns.push({
    label: "Total",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaItemComposicao).total,
  });

  return columns;
}

function getColumnsTotais() {
  const columns: IColumn[] = [];

  columns.push({
    label: "Descrição",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaDetailsItem).descricao,
  });

  columns.push({
    label: "Vencimento",
    value: (row: IContent) =>
      moment(
        (row as unknown as CobrancaContaDetailsItem).dataVencimento
      ).format("DD/MM/YYYY"),
  });

  columns.push({
    label: "Dias atraso",
    format: "#",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaDetailsItem).diasEmAtraso,
  });

  columns.push({
    label: "Original",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaDetailsItem).valorOriginal,
  });

  columns.push({
    label: "Multa",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaDetailsItem).multa,
  });

  columns.push({
    label: "Juros",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaDetailsItem).juros,
  });

  columns.push({
    label: "Tx. Cobrança",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaDetailsItem).valorTaxaCobranca,
  });

  columns.push({
    label: "Correção Monetária",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaDetailsItem).atualizacaoMonetaria,
  });

  // columns.push({
  //   label: "Honorários",
  //   format: "#,###0.00",
  //   value: (row: IContent) =>
  //   (row as unknown as CobrancaContaDetailsItem).honorarios
  // });

  columns.push({
    label: "Total",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as CobrancaContaDetailsItem).total,
  });

  return columns;
}

interface ColumnResumoItem {
  unidade: string;
  valorOriginal: number;
  total: number;
  dataCalculo: string;
}

function getColumnsResumo() {
  const columns: IColumn[] = [];

  columns.push({
    label: "Unidade",
    value: (row: IContent) => (row as unknown as ColumnResumoItem).unidade,
  });

  columns.push({
    label: "Valor original",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as ColumnResumoItem).valorOriginal,
  });

  columns.push({
    label: "Total",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as ColumnResumoItem).total,
  });

  columns.push({
    label: "Data de cálculo",
    value: (row: IContent) => (row as unknown as ColumnResumoItem).dataCalculo,
  });
  return columns;
}
